import type { LocalEmployee } from '../services/localEmployeesStore';
import type {
  EmployeeGradingOverride,
  PositionGrading,
} from '../services/localEmployeeGradingStore';
import type { MarketDataRecord } from '../services/marketDataService';
import {
  marketFixAnnualToMonthly,
  marketTotalCompensationAnnual,
} from './marketCompensationUtils';
import type { RoleClusterMeta } from './marketClusterMeta';
import {
  clusterGroupKey,
  filterRecordsByMarketGroup,
  filterRecordsWithMarketGroup,
  getSortedMarketGroups,
  type MarketGroupingMode,
  type MarketGroup,
} from './marketGroupUtils';

export type ResolvedEmployeeGrading = {
  source: 'employee' | 'position';
  familyId: number;
  familyName: string;
  roleClusterId: number;
  roleClusterName: string;
  roleId: number;
  roleName: string;
  gradeLevel: number;
};

export type MarketBenchmark = {
  fix: number[];
  sti: number[];
  total: number[];
  yes: number;
  n: number;
};

function normalizePositionKey(position: string): string {
  return position.trim().toLowerCase();
}

export function resolveEmployeeGrading(
  employee: LocalEmployee,
  positionGradings: PositionGrading[],
  employeeOverrides: EmployeeGradingOverride[],
): ResolvedEmployeeGrading | null {
  const override = employeeOverrides.find((item) => item.employeeId === employee.id);
  if (override) {
    return {
      source: 'employee',
      familyId: override.familyId,
      familyName: override.familyName,
      roleClusterId: override.roleClusterId,
      roleClusterName: override.roleClusterName,
      roleId: override.roleId,
      roleName: override.roleName,
      gradeLevel: override.gradeLevel,
    };
  }

  const positionKey = normalizePositionKey(employee.position);
  const positionGrading = positionGradings.find(
    (item) => normalizePositionKey(item.position) === positionKey,
  );
  if (!positionGrading) return null;

  return {
    source: 'position',
    familyId: positionGrading.familyId,
    familyName: positionGrading.familyName,
    roleClusterId: positionGrading.roleClusterId,
    roleClusterName: positionGrading.roleClusterName,
    roleId: positionGrading.roleId,
    roleName: positionGrading.roleName,
    gradeLevel: positionGrading.gradeLevel,
  };
}

export function employeeMarketGroupKey(
  grading: ResolvedEmployeeGrading,
  mode: MarketGroupingMode,
): string {
  if (mode === 'cluster') {
    return clusterGroupKey(grading.familyId, grading.roleClusterId);
  }
  return `${grading.familyName.toLowerCase()}::${grading.gradeLevel}`;
}

export function buildBenchmarkFromRecords(records: MarketDataRecord[]): MarketBenchmark {
  const fix: number[] = [];
  const sti: number[] = [];
  const total: number[] = [];
  let yes = 0;

  for (const record of records) {
    if (Number.isFinite(record.fix)) fix.push(marketFixAnnualToMonthly(record.fix));
    if (Number.isFinite(record.sti)) sti.push(record.sti);
    if (Number.isFinite(record.fix) && Number.isFinite(record.sti)) {
      total.push(marketTotalCompensationAnnual(record.fix, record.sti));
    }
    if ((record.ltip || '').toString().toLowerCase() === 'да') yes += 1;
  }

  fix.sort((a, b) => a - b);
  sti.sort((a, b) => a - b);
  total.sort((a, b) => a - b);

  return { fix, sti, total, yes, n: records.length };
}

export function buildMarketBenchmarksByGroup(
  filteredMarketData: MarketDataRecord[],
  mode: MarketGroupingMode,
  clusterMetas: RoleClusterMeta[],
): Map<string, MarketBenchmark> {
  const gradedRecords = filterRecordsWithMarketGroup(filteredMarketData, mode, clusterMetas);
  const groups = getSortedMarketGroups(gradedRecords, mode, clusterMetas);
  const map = new Map<string, MarketBenchmark>();

  for (const group of groups) {
    const records = filterRecordsByMarketGroup(gradedRecords, group.key, mode, clusterMetas);
    map.set(group.key, buildBenchmarkFromRecords(records));
  }

  return map;
}

export function resolveComparisonGroupingMode(
  mode: MarketGroupingMode,
  clusterMetas: RoleClusterMeta[],
): MarketGroupingMode {
  if (mode === 'cluster' && clusterMetas.length === 0) return 'grade';
  return mode;
}

export function employeeBenchmarkLabel(
  grading: ResolvedEmployeeGrading,
  mode: MarketGroupingMode,
  group?: MarketGroup,
): string {
  if (mode === 'cluster') {
    return group?.label || grading.roleClusterName;
  }
  return grading.roleName || `G${grading.gradeLevel}`;
}

export function employeeGradingSubtitle(
  grading: ResolvedEmployeeGrading,
  mode: MarketGroupingMode,
): string {
  if (mode === 'cluster') {
    return `${grading.roleClusterName} · ${grading.roleName}`;
  }
  return `${grading.roleName} · G${grading.gradeLevel}`;
}
